'use client'

import { useEffect } from 'react'
import { useLenis } from 'lenis/react'
import { Typography } from 'src/components/design-system'
import { Pagination } from 'src/components/list/Pagination'
import ProductTileGrid from 'src/components/list/ProductTileGrid'
import { useTranslateEntry } from 'src/i18n/client/useTranslateEntry'

/**
 * Wspólny renderer wyników listy:
 * - stan ładowania,
 * - stan "brak wyników",
 * - grid z kafelkami + dolna paginacja.
 *
 * Dzięki temu każdy widok listy nie powiela tych samych warunków JSX.
 */
export default function PagedTileGrid({
  tiles = [],
  isLoading,
  page,
  totalPages,
  onPageChange,
  desktopColumns = 4,
  withSidebar = false,
  paginationVariant = 'open-access',
  paginationClassName = 'justify-center mb-32',
  tileClassName = '',
}) {
  const translateEntry = useTranslateEntry()
  const lenis = useLenis()

  useEffect(() => {
    const frame = requestAnimationFrame(() => lenis?.resize())

    return () => cancelAnimationFrame(frame)
  }, [isLoading, lenis, page, tiles.length])

  if (!Array.isArray(tiles) || tiles.length === 0) {
    return (
      <Typography variant="body3-normal" className="mt-25 mb-32 text-center" color="text-basic-gray2">
        {translateEntry(isLoading ? 'T_LOADING' : 'T_NO_RESULTS')}
      </Typography>
    )
  }

  return (
    <div className="relative" aria-busy={isLoading}>
      <div
        className={`transition-opacity duration-200 ${isLoading ? 'pointer-events-none opacity-40' : 'opacity-100'}`}
      >
        <ProductTileGrid
          tiles={tiles}
          desktopColumns={desktopColumns}
          withSidebar={withSidebar}
          tileClassName={tileClassName}
        />
        <Pagination
          className={paginationClassName}
          page={page}
          pages={totalPages}
          setPage={onPageChange}
          variant={paginationVariant}
        />
      </div>

      {isLoading && (
        <Typography
          variant="body3-normal"
          component="p"
          role="status"
          className="absolute inset-x-0 top-25 z-10 text-center"
          color="text-basic-gray2"
        >
          {translateEntry('T_LOADING')}
        </Typography>
      )}
    </div>
  )
}
